// deploy-version detection. the running bundle carries a build stamp baked in at
// build time (git commit hash, via the VITE_APP_VERSION build arg); the same
// stamp is written to a non-cached /version.json at the web root. comparing the
// two lets a long-lived tab notice that a newer frontend has been deployed and
// offer to reload — the non-forced half of the hybrid strategy whose forced half
// is the stale-chunk hard reload in reloadOnPreloadError.ts.

// the stamp compiled into this bundle. empty for a plain local `vite dev`/build
// (no build arg), which disables the watcher — see isVersionWatchEnabled.
export const APP_VERSION = (import.meta.env.VITE_APP_VERSION ?? '').trim()

// the marker served (uncached) at the web root, carrying the deployed build's
// stamp. a leading slash keeps it root-relative regardless of the current route.
export const VERSION_ENDPOINT = '/version.json'

// only watch when the running bundle carries a real stamp. a dev build has none,
// so local development never raises a spurious "new version" prompt.
export const isVersionWatchEnabled = APP_VERSION.length > 0

// fetch the deployed build's stamp from the uncached marker. returns null on any
// failure — network blip, a 404 (dev has no marker), or a malformed body — so a
// transient error or missing marker never surfaces as a phantom update.
export const fetchDeployedVersion = async (signal?: AbortSignal): Promise<string | null> => {
  try {
    const response = await fetch(VERSION_ENDPOINT, { cache: 'no-store', signal })
    if (!response.ok) return null
    const data = (await response.json()) as { version?: unknown }
    return typeof data.version === 'string' && data.version.length > 0 ? data.version : null
  } catch {
    return null
  }
}

// a newer deploy is a non-empty deployed stamp that differs from the running one.
// an empty `current` (local dev / no stamp) is never considered newer, keeping
// the comparison self-guarding and independent of the module constant so it can
// be unit-tested directly.
export const isNewerVersion = (current: string, deployed: string | null): boolean =>
  current.length > 0 && deployed !== null && deployed !== current
